/**
 * This service is used to manage the app bar.
 * It is used to display the title and the template; or only the title, or only the template.
 * It is also used to show/hide the back button.
 * @see {@link AppBarComponent} for more info.
 * By default the title and back button are displayed by route data.
 * An example of usage for template can be found in
 * @see {@link ListaAnagraficheComponent}
 */
import { Injectable, TemplateRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AppBarService {
  /**
   * The title of the app bar.
   */
  private titleSource = new BehaviorSubject<string>('PAGINA SENZA TITOLO');

  /**
   * The template of the app bar.
   */
  private templateSource = new BehaviorSubject<TemplateRef<null> | null>(null);

  /**
   * A flag to show/hide the back button.
   */
  private showBackButtonSource = new BehaviorSubject<boolean>(false);

  /**
   * The url to go back.
   */
  private onClickBackGoTo = new BehaviorSubject<string>('');

  /**
   * Public observables.
   */
  pageTitle$ = this.titleSource.asObservable();
  pageTemplate$$ = this.templateSource.asObservable();
  showBackButton$ = this.showBackButtonSource.asObservable();
  onClickBackGoTo$ = this.onClickBackGoTo.asObservable();

  /**
   * @param titleKey The title of the route to display in the app bar.
   */
  setTitle(titleKey: string | null): void {
    this.titleSource.next(titleKey);
  }

  /**
   * @param template The template to display in the app bar.
   */
  setTemplate(template: TemplateRef<null> | null): void {
    this.templateSource.next(template);
  }

  /**
   * @param show The flag to show/hide the back button.
   */
  setShowBackButton(show: boolean): void {
    this.showBackButtonSource.next(show);
  }

  /**
   * @param url The url to go back.
   */
  setGoBackToUrl(url: string): void {
    this.onClickBackGoTo.next(url);
  }
}
